<template>
    <v-card class="my-2" variant="outlined">
        <v-list-item lines="three" :class="fullCard ? 'pl-0' : ''">
            <template v-slot:prepend>
                <v-img
                    v-if="fullCard"
                    tile
                    class="thumb my-0"
                    :src="$filters.imageUrl(mainImage.url, 200)"
                    :alt="info.serviceName"
                    :lazy-src="$filters.srcHost('/placeholder.png')"
                    :width="!$breakpoint.smAndDown ? '125px' : '25%'"
                    @error="onImgError"
                >
                    <template v-slot:placeholder>
                        <v-row
                            v-show="!imgError && mainImage.url"
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                        >
                            <v-progress-circular indeterminate color="grey-lighten-5" />
                        </v-row>
                    </template>
                </v-img>
            </template>
            <div class="d-flex flex-column flex-md-row ms-5">
                <div class="align-self-start" :class="{'pl-2': !fullCard}">
                    <div
                        class="wrap-words d-flex align-center text-subtitle-1 font-weight-bold cursor-pointer mb-1"
                        @click="offerInfo()"
                    >
                        <v-icon start size="small">mdi-information-outline</v-icon>
                        <div>
                            {{ info.serviceName }}
                        </div>
                    </div>
                    <div v-if="discountDetails" class="d-flex flex-shrink-0 width-max-content">
                        <special-offer-label :details="discountDetails" />
                    </div>
                    <div>
                        <span v-if="offer.available" class="d-flex align-center text-caption">
                            <v-icon start :title="$t('available')" size="small" class="text-success">
                                mdi-checkbox-marked-circle
                            </v-icon>
                            <span class="text-success" v-text="$t('available')" />
                        </span>
                        <span v-else class="d-flex align-center mt-1 text-caption">
                            <v-icon start :title="$t('on_request')" size="small" class="text-warning"
                                >mdi-alert-circle</v-icon
                            >
                            <span class="text-warning" v-text="$t('on_request')" />
                        </span>
                    </div>
                    <discount-label v-if="offer.price" :price="offer.price" class="d-inline-block" />
                    <div class="d-flex align-center text-caption">
                        <v-icon size="small" start>mdi-calendar</v-icon>
                        <span>
                            {{ $filters.dateFormat(offer.startDate) }}
                        </span>
                    </div>
                    <span v-if="offer && offer.price && showPriceDuringSearch" class="text-caption">
                        <conditions-label
                            :offer-key="offer.offerKey"
                            :price="offer.price"
                            @conditions="showConditionsInfo"
                        />
                    </span>
                    <div v-if="offer.info.tariff" class="d-flex align-center text-caption">
                        <v-icon start size="small" class="text-info">mdi-tag-text</v-icon>
                        <span>
                            {{ offer.info.tariff }}
                        </span>
                    </div>
                </div>
                <v-spacer />
                <div class="offer-price align-self-end align-self-md-center d-flex flex-column align-center">
                    <slot name="price">
                        <template v-if="!offer.soldOut">
                            <v-btn
                                :loading="!info.productName"
                                :disabled="!info.productName"
                                color="primary"
                                height="36px"
                                max-height="36px"
                                block
                                size="small"
                                no-prefetch
                                data-cy="client-price"
                                @click="action(selectForBooking)"
                            >
                                {{ label($filters.price(offer.price)) }}
                            </v-btn>
                            <template v-if="showPriceDuringSearch">
                                <commission-amount :commission="offer.price.commission" />
                                <net-amount :search-request="searchRequest" :price="offer.price" />
                            </template>
                        </template>
                        <sold-out-modal v-else small />
                    </slot>
                </div>
            </div>
        </v-list-item>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Setup, Vue} from 'vue-facing-decorator'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import ConditionsLabel from '~src/components/snippets/conditionsLabel.src'
    import {EventBus, SHOW_EXTRA_SERVICE_CONDITIONS_INFO, SHOW_EXTRA_SERVICE_INFO_MODAL} from '@/utils/event-bus'
    import NetAmount from '~src/components/snippets/NetAmount.vue'
    import SpecialOfferLabel from '~/components/hotels/search/offers/SpecialOfferLabel.vue'
    import SoldOutModal from '~src/components/snippets/SoldOutModal.vue'

    @Component({
        components: {
            SoldOutModal,
            SpecialOfferLabel,
            CommissionAmount,
            DiscountLabel,
            ConditionsLabel,
            NetAmount,
        },
    })
    export class ExtraServiceOfferCard extends Vue {
        @Prop() offer
        @Prop({default: true}) fullCard
        @Prop({default: ''}) description
        @Setup(() => useBookingPriceButton().action) action
        @Setup(() => useBookingPriceButton().label) label
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        imgError = false
        info = {
            images: [],
        }

        async mounted() {
            try {
                this.info = await this.$api.ownExtraServiceInfo.get({offerKey: this.offer.offerKey})
                if (this.fullCard) {
                    this.$emit('description', this.info.productDescription)
                }
                // eslint-disable-next-line no-empty
            } catch (e) {}
            this.info.description = this.description
            this.info.service = this.offer.info.service
            this.info.tariff = this.offer.info.tariff
        }

        onImgError() {
            if (this.mainImage.url) {
                this.imgError = true
            }
        }

        selectForBooking() {
            extraServicesStore.ADD_TO_BASKET({info: this.info, offerKey: this.offer.offerKey})
            this.$router.push({
                name: 'extraServiceBooking',
                query: {offerKey: this.offer.offerKey},
            })
        }

        offerInfo() {
            EventBus.$emit(SHOW_EXTRA_SERVICE_INFO_MODAL, {offer: this.offer, info: this.info})
        }

        showConditionsInfo() {
            EventBus.$emit(SHOW_EXTRA_SERVICE_CONDITIONS_INFO, this.offer)
        }

        get mainImage() {
            const mainImage = this.info.images?.find(image => image.mainImage) || {url: null}
            let serviceImage = mainImage
            if (!serviceImage.url && this.info.images?.length > 0) {
                serviceImage = this.info.images[0]
            }
            if (
                !serviceImage.linkedEntityName ||
                (serviceImage.linkedEntityName && mainImage.linkedEntityName !== this.info.serviceName)
            ) {
                serviceImage = this.info.images?.find(image => image.linkedEntityName === this.info.serviceName) || {
                    url: null,
                }
            }
            return serviceImage
        }

        get isAgency() {
            return authStore.isAgency
        }

        get discountDetails() {
            return this.offer?.price?.discountDetails
        }
    }

    export default ExtraServiceOfferCard
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .offer-price {
        width: 20%;
    }
    @media #{map.get(settings.$display-breakpoints, 'xs')} {
        .offer-price {
            width: 25%;
        }
    }

    .wrap-words {
        white-space: normal;
    }
</style>
